import { MarkDown, SwitchField, Text, TextField } from "@wayflyer/flyui";
import type { ReferencedStackedBarChartProps } from "@wayflyer/flyui-dataviz";
import { ReferencedStackedBarChart } from "@wayflyer/flyui-dataviz";
import { useIntl } from "react-intl";
import { styled } from "styled-components";

import { Spacer } from "../../../../components/shared";
import { TabTitle } from "../../../../components/tab-title";
import type { EventHandler } from "../../../../utils/types";

export interface ConsumersChurnTabProps {
  chartData: ReferencedStackedBarChartProps;
  churnPeriodLabel?: string;
  churnPeriod?: string;
  churnPeriodUnit?: string;
  churnPeriodTooltip?: string;
  onChangeChurnPeriod: EventHandler;
  summaryText?: string;
  toggleProps?: ToggleProps;
  title: string;
  tooltip?: string;
}

interface ToggleProps {
  label: string;
  on: boolean;
  onChange: (isOn: boolean) => void;
  tooltip: string;
}

const ChartContainer = styled.div`
  width: 100%;
`;

const TabContainer = styled.div`
  display: flex;
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const ChurnPeriodContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  max-width: 120px;
`;

const PeriodUnitContainer = styled.div`
  white-space: nowrap;
  padding-left: 5px;
  padding-bottom: 10px;
`;

const InputContainer = styled.div`
  display: flex;
  align-items: baseline;
`;

/**
 * ConsumersChurnTab component
 */
export const ConsumersChurnTab = ({
  chartData,
  onChangeChurnPeriod,
  churnPeriod = "3",
  churnPeriodLabel,
  churnPeriodUnit,
  churnPeriodTooltip,
  summaryText,
  toggleProps,
  title,
  tooltip,
}: ConsumersChurnTabProps): UIElement => {
  const intl = useIntl();
  const label =
    churnPeriodLabel || intl.formatMessage({ id: "Churn Tab: Churn Period", defaultMessage: "Churn Period" });

  let toggleElement: React.JSX.Element | null = null;
  if (toggleProps) {
    toggleElement = (
      <SwitchField
        label={toggleProps.label}
        name={toggleProps.label}
        variant="full-width"
        isSelected={toggleProps.on}
        helperText={toggleProps.tooltip}
        onChange={toggleProps.onChange}
      />
    );
  }

  return (
    <>
      <TabTitle title={title} tooltip={tooltip} />
      {summaryText ? <MarkDown markdown={summaryText} /> : null}
      <Spacer />
      <TabContainer>
        <ChartContainer>
          <ReferencedStackedBarChart {...chartData} />
        </ChartContainer>
        <div>
          <ChurnPeriodContainer>
            <InputContainer>
              <TextField
                label={label}
                value={`${churnPeriod}`}
                name={label}
                onChange={onChangeChurnPeriod}
                tooltip={
                  churnPeriodTooltip ??
                  intl.formatMessage({
                    id: "Consumers Tab: Define the churn period here",
                    defaultMessage: "Define the churn period here",
                  })
                }
              />
              {churnPeriodUnit ? (
                <PeriodUnitContainer>
                  <Text>{churnPeriodUnit}</Text>
                </PeriodUnitContainer>
              ) : null}
            </InputContainer>
            {toggleElement}
          </ChurnPeriodContainer>
        </div>
      </TabContainer>
    </>
  );
};
